import React, { ReactNode } from 'react';
import { TableBody, TableRow, TableCell, Typography, IconButton, Box, Collapse } from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { DataTableColumn, DataTableSelection, DataTableExpansion, NestedTableRenderProps } from '../tableTypes';
import { renderCellContent } from '@core/utils/tableUtils.tsx';
import { useTranslation } from 'react-i18next';
import WcnCheckbox from '@/components/shared/WcnCheckbox';

interface WcnTableBodyProps<T> {
    readonly paginatedData: T[];
    readonly columns: DataTableColumn<T>[];
    readonly colSpan: number;
    readonly hasExpandableRows: boolean;
    readonly shouldAutoCard: boolean;
    readonly loading?: boolean;
    readonly selection?: DataTableSelection<T>;
    readonly expansion?: DataTableExpansion<T>;
    readonly expandedRows: (string | number)[];
    readonly noDataMessage?: string;
    readonly renderEmpty?: () => ReactNode;
    readonly onRowClick?: (row: T, index: number) => void;
    readonly getRowProps?: (row: T, index: number) => Record<string, any>;
    readonly onRowSelection: (row: T, event?: React.MouseEvent) => void;
    readonly onRowExpansion: (row: T, event?: React.MouseEvent) => void;
    /** Re-render nested tables with the same mobileView mode. */
    readonly renderNestedTable: (props: NestedTableRenderProps) => ReactNode;
}

function WcnTableBody<T>({
    paginatedData,
    columns,
    colSpan,
    hasExpandableRows,
    shouldAutoCard,
    loading,
    selection,
    expansion,
    expandedRows,
    noDataMessage,
    renderEmpty,
    onRowClick,
    getRowProps,
    onRowSelection,
    onRowExpansion,
    renderNestedTable
}: Readonly<WcnTableBodyProps<T>>) {
    const { t } = useTranslation();

    if (!paginatedData || paginatedData.length === 0) {
        return (
            <TableBody>
                <TableRow>
                    <TableCell colSpan={colSpan} align="center">
                        {renderEmpty ? (
                            renderEmpty()
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                {loading ? t('loadingData') : noDataMessage}
                            </Typography>
                        )}
                    </TableCell>
                </TableRow>
            </TableBody>
        );
    }

    return (
        <TableBody className={shouldAutoCard ? 'card-view' : ''}>
            {paginatedData.map((row, rowIndex) => {
                const isSelected = selection
                    ? selection.selectedItems.some((item) => selection.getItemKey(item) === selection.getItemKey(row))
                    : false;
                const isExpanded = expansion ? expandedRows.includes(expansion.getRowKey(row)) : false;
                const rowProps = getRowProps?.(row, rowIndex) || {};
                const showExpandIcon = expansion?.showExpandIconCondition?.(row) ?? true;

                return (
                    <React.Fragment key={expansion?.getRowKey(row) ?? rowIndex}>
                        <TableRow
                            hover
                            selected={isSelected}
                            onClick={(e: React.MouseEvent) => {
                                if (selection && !expansion) onRowSelection(row, e);
                                onRowClick?.(row, rowIndex);
                            }}
                            sx={{
                                cursor: onRowClick || selection ? 'pointer' : 'default',
                                willChange: 'transform',
                                '&:focus-within': { position: 'relative', zIndex: 2 },
                                ...rowProps.sx
                            }}
                            className={`data-row ${rowIndex % 2 === 0 ? 'even-row' : 'odd-row'}`}
                            {...rowProps}
                        >
                            {hasExpandableRows && (
                                <TableCell>
                                    {showExpandIcon && (
                                        <IconButton
                                            size="small"
                                            onClick={(e: React.MouseEvent) => onRowExpansion(row, e)}
                                        >
                                            {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                        </IconButton>
                                    )}
                                </TableCell>
                            )}

                            {selection && (
                                <TableCell align="center" padding="checkbox" className="table-checkbox-cell">
                                    <WcnCheckbox
                                        className="table-checkbox"
                                        checked={isSelected}
                                        onClick={(e: React.MouseEvent) => onRowSelection(row, e)}
                                    />
                                </TableCell>
                            )}

                            {columns.map((column) => {
                                const cellTitle = typeof column.title === 'function' ? column.title(row) : column.title;
                                return (
                                    <TableCell
                                        key={column.value}
                                        align={column.align || 'left'}
                                        className={[
                                            'data-row',
                                            rowIndex % 2 === 0 ? 'even-row' : 'odd-row',
                                            column.multiLine ? 'cell--multi-line' : ''
                                        ]
                                            .filter(Boolean)
                                            .join(' ')}
                                        title={cellTitle}
                                        sx={column.multiLine ? { py: 1.5 } : undefined}
                                    >
                                        {renderCellContent(column, row, rowIndex)}
                                    </TableCell>
                                );
                            })}
                        </TableRow>

                        {expansion && isExpanded && (
                            <TableRow>
                                <TableCell
                                    colSpan={colSpan}
                                    className="shop-expanded-row"
                                    sx={{ p: 0, borderBottom: 0 }}
                                >
                                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                        <Box>
                                            {expansion.nestedTable ? (
                                                <Box sx={{ pl: expansion.nestedTable.paddingLeft ?? 8 }}>
                                                    {renderNestedTable({
                                                        columns: expansion.nestedTable.columns,
                                                        data: expansion.nestedTable.getChildRows(
                                                            row,
                                                            expansion.nestedTable.data
                                                        ),
                                                        size: expansion.nestedTable.size ?? 'small',
                                                        noDataMessage: expansion.nestedTable.noDataMessage,
                                                        expansion: expansion.nestedTable.expansion
                                                    })}
                                                </Box>
                                            ) : (
                                                expansion.renderExpandedContent?.(row)
                                            )}
                                        </Box>
                                    </Collapse>
                                </TableCell>
                            </TableRow>
                        )}
                    </React.Fragment>
                );
            })}
        </TableBody>
    );
}

WcnTableBody.displayName = 'WcnTableBody';

export default React.memo(WcnTableBody) as typeof WcnTableBody;
